// @owners { team: patients }
import { Body, LgSpacing, LoadingEmptyState, MdSpacing, XxlPadding } from '@alto/design-system';
import { type SupportCase } from '@alto/scriptdash/alto/customer_support/types/v1/support_case';
import React, { useEffect } from 'react';
import { FlatList, type ListRenderItemInfo } from 'react-native';
import { getCompanyName } from '~shared/config/getCompanyName';
import { ASSISTANT_VIEWED_EVENTS } from '~shared/features/alto-assistant/analytics/constants';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { getUnreadSupportCaseCount, useGetSupportCases } from '../../queries/queries';
import { SupportCaseCard } from './SupportCaseCard';

export const Inbox = () => {
  const { supportCases, fetchNextPage, hasNextPage, isPending } = useGetSupportCases();
  const { trackEvent } = useAnalytics();
  const unreadSupportCaseCount = getUnreadSupportCaseCount(supportCases);

  const handleFetchMore = async () => {
    if (hasNextPage && !isPending) {
      trackEvent({
        event: EVENTS.ASSISTANT_COMPONENT_VIEWED,
        params: {
          name: ASSISTANT_VIEWED_EVENTS.FETCH_MORE_SUPPORT_CASES,
          unreadSupportCaseCount,
        },
      });
      await fetchNextPage();
    }
  };

  const renderSupportCase = (item: ListRenderItemInfo<SupportCase>) => {
    return (
      <React.Fragment key={`support-case-${item.item.id}}`}>
        <SupportCaseCard supportCase={item.item} />
        <MdSpacing />
      </React.Fragment>
    );
  };

  useEffect(() => {
    trackEvent({
      event: EVENTS.ASSISTANT_COMPONENT_VIEWED,
      params: {
        name: ASSISTANT_VIEWED_EVENTS.SUPPORT_CASE_INBOX,
        unreadSupportCaseCount,
      },
    });
  }, [trackEvent, unreadSupportCaseCount]);

  if (isPending) {
    return (
      <>
        <LgSpacing />
        <XxlPadding>
          <LoadingEmptyState />
        </XxlPadding>
      </>
    );
  }

  if (supportCases.length === 0) {
    return (
      <>
        <LgSpacing />
        <XxlPadding>
          <Body center>{`All messages you have with ${getCompanyName()} show up here`}</Body>
        </XxlPadding>
      </>
    );
  }

  return (
    <FlatList
      nestedScrollEnabled
      data={supportCases}
      renderItem={renderSupportCase}
      scrollEnabled={false}
      keyExtractor={(item) => item.id.toString()}
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onEndReached={handleFetchMore}
      onEndReachedThreshold={0.1}
    />
  );
};
